import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'
import { SyncOperation } from '../DTOs/Sync/SyncDtos'
import type { OperationMeterMutation, SyncOperationType, SyncRequest } from '../DTOs/Sync/SyncDtos'
import { store } from '../state/store'
import { clearProvisionalShift } from '../state/reducers/shiftReducer'
import type { MutationQueueItem, OfflineEntity } from './db'
import { refreshLatestShift } from './refreshLatestShift'

type Base = {
  clientId: string
  operation: SyncOperationType
  shiftId: number
  rowVersion?: string
  data: unknown
}

type Child = { entity: OfflineEntity; field: string }

export interface SyncEntity {
  idField?: string
  children?: Child[]
  add: (request: SyncRequest, item: MutationQueueItem, base: Base) => void
  stale: QueryKey[] | ((queryClient: QueryClient) => Promise<void>)
}

const CONSUMABLE_KEY = /^(consumable|drillComponent|fittableComponents|componentLife|parkedConsumable)/

function isConsumableQuery(queryKey: QueryKey) {
  const [root, name] = queryKey[0] === 'dataTable' ? queryKey.slice(1) : queryKey
  return root === 'drillBlast' && CONSUMABLE_KEY.test(String(name))
}

const DRILLING_STALE: QueryKey[] = [['drilling'], ['dataTable', 'drilling'], ['drillBlast', 'rcSession']]

const operationCode = (op: MutationQueueItem['operation']) =>
  op === 'create' ? SyncOperation.Create : op === 'update' ? SyncOperation.Update : SyncOperation.Delete

export const SYNC_ENTITIES: Record<OfflineEntity, SyncEntity> = {
  stoppage: {
    idField: 'stoppageId',
    add: (request, item, base) => {
      const { plant: _p, stoppageType: _st, stoppageSubType: _sst, location: _l, ...safeData } = (base.data ?? {}) as Record<string, unknown>
      request.stoppages.push({ ...base, data: safeData, stoppageId: item.entityId })
    },
    stale: [['dataTable', 'operations'], ['dataTable', 'stoppages']],
  },
  loading: {
    idField: 'loadingId',
    add: (request, item, base) => request.loadings.push({ ...base, loadingId: item.entityId }),
    stale: [['dataTable', 'chatree', 'truckLoads'], ['dataTable', 'chatree', 'diggingLoadingSummary']],
  },
  diggingPattern: {
    idField: 'diggingPatternId',
    children: [{ entity: 'loading', field: 'diggingPatternId' }],
    add: (request, item, base) => request.diggingPatterns.push({ ...base, diggingPatternId: item.entityId }),
    stale: [['dataTable', 'chatree', 'diggerLocations'], ['dataTable', 'chatree', 'diggingLoadingSummary']],
  },
  completeShift: {
    add: (request, item) => request.completeShifts.push({ clientId: item.clientId, shiftId: item.shiftId }),
    stale: async queryClient => {
      store.dispatch(clearProvisionalShift())
      await refreshLatestShift()
      for (const queryKey of [['dataTable'], ['chatree'], ['production'], ['geology'], ['shiftExistence'], ['operations']])
        await queryClient.invalidateQueries({ queryKey })
    },
  },
  operationLocation: {
    add: (request, item) => {
      const d = (item.data ?? {}) as { operationId?: number; locationId?: number; operationPlantId?: number }
      request.operationLocations.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        locationId: d.locationId ?? 0,
        shiftId: item.shiftId,
        operationPlantId: d.operationPlantId,
      })
    },
    stale: [['dataTable', 'operations'], ['operations']],
  },
  engineHours: {
    add: (request, item) => {
      const d = (item.data ?? {}) as { operationId?: number; startEngineHours?: number; endEngineHours?: number; fuel?: number | null; operationPlantId?: number }
      request.engineHours.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        shiftId: item.shiftId,
        startEngineHours: d.startEngineHours,
        endEngineHours: d.endEngineHours,
        fuel: d.fuel ?? undefined,
        operationPlantId: d.operationPlantId,
      })
    },
    stale: [['dataTable', 'operations'], ['operations']],
  },
  geoRehandle: {
    idField: 'geoRehandleId',
    add: (request, item) => request.geoRehandles.push({
      clientId: item.clientId, operation: operationCode(item.operation), geoRehandleId: item.entityId, rowVersion: item.rowVersion, shiftId: item.shiftId, data: item.data,
    }),
    stale: [['dataTable', 'geology', 'rehandles'], ['dataTable', 'geology', 'crushing']],
  },
  drillingSession: {
    children: [{ entity: 'drillingPattern', field: 'drillingId' }, { entity: 'gcLine', field: 'drillingId' }],
    add: (request, item) => {
      const d = (item.data ?? {}) as { rigId: number; drillingTypeId: number }
      request.drillingSessions.push({ clientId: item.clientId, shiftId: item.shiftId, rigId: d.rigId, drillingTypeId: d.drillingTypeId, drillingId: item.entityId })
    },
    stale: DRILLING_STALE,
  },
  drillingPattern: {
    children: [{ entity: 'drillingLine', field: 'drillingBlastId' }, { entity: 'drilledHole', field: 'drillingBlastId' }],
    add: (request, item) => {
      const d = (item.data ?? {}) as { drillingId: number; patternBlastId: number; entryMode?: number }
      request.drillingPatterns.push({
        clientId: item.clientId,
        operation: operationCode(item.operation),
        shiftId: item.shiftId,
        drillingBlastId: item.entityId,
        drillingId: d.drillingId,
        patternBlastId: d.patternBlastId,
        entryMode: d.entryMode ?? 0,
      })
    },
    stale: DRILLING_STALE,
  },
  drillingLine: {
    add: (request, item, base) => request.drillingLines.push({
      clientId: item.clientId, operation: base.operation, shiftId: item.shiftId, lineId: item.entityId, rowVersion: item.rowVersion, data: base.data,
    }),
    stale: DRILLING_STALE,
  },
  operationMeter: {
    add: (request, item, base) => request.operationMeters.push({ ...(base.data as Omit<OperationMeterMutation, 'clientId' | 'shiftId'>), clientId: item.clientId, shiftId: item.shiftId }),
    stale: [['drillBlast', 'rigMeters']],
  },
  gcLine: {
    add: (request, item, base) => request.gcLines.push({
      clientId: item.clientId, operation: base.operation, shiftId: item.shiftId, lineId: item.entityId, rowVersion: item.rowVersion, data: base.data,
    }),
    stale: [['drillBlast', 'rcSession']],
  },
  drilledHole: {
    add: (request, item, base) => request.drilledHoles.push({
      clientId: item.clientId, operation: base.operation, shiftId: item.shiftId, attemptId: item.entityId, rowVersion: item.rowVersion, data: base.data,
    }),
    stale: [['drillBlast', 'drillingSessionHoles'], ['drillBlast', 'patternHoles'], ['drilling']],
  },
  holeDip: {
    add: (request, item, base) => request.holeDips.push({
      clientId: item.clientId, operation: base.operation, dipId: item.entityId, rowVersion: item.rowVersion, data: base.data,
    }),
    stale: [['drillBlast', 'patternDips'], ['drillBlast', 'patternHoles'], ['drillBlast', 'pattern']],
  },
  holeCharge: {
    add: (request, item, base) => request.holeCharges.push({ clientId: item.clientId, blastHoleId: item.entityId ?? 0, data: base.data }),
    stale: [['drillBlast', 'patternCharging'], ['drillBlast', 'patternHoles'], ['drillBlast', 'pattern'], ['dataTable', 'references', 'blastMaterialUsage']],
  },
  explosiveMovement: {
    add: (request, item, base) => request.explosiveMovements.push({ clientId: item.clientId, data: base.data }),
    stale: [['drillBlast', 'explosivesStock'], ['dataTable', 'drillBlast', 'explosivesTransactions'], ['drillBlast', 'parkedMovements']],
  },
  firing: {
    add: (request, item, base) => {
      const d = (base.data ?? {}) as { patternBlastId: number; firing: unknown }
      request.firings.push({
        clientId: item.clientId, operation: base.operation, patternBlastId: d.patternBlastId, firingId: item.entityId, rowVersion: item.rowVersion, data: d.firing,
      })
    },
    stale: [['drillBlast', 'firing'], ['drillBlast', 'pattern'], ['drillBlast', 'openMisfires']],
  },
  misfire: {
    add: (request, item, base) => {
      const d = (base.data ?? {}) as { patternBlastId: number; misfire: unknown }
      request.misfires.push({ clientId: item.clientId, patternBlastId: d.patternBlastId, data: d.misfire })
    },
    stale: [['drillBlast', 'firing'], ['drillBlast', 'pattern'], ['drillBlast', 'openMisfires']],
  },
  consumableMovement: {
    add: (request, item, base) => request.consumableMovements.push({ clientId: item.clientId, data: base.data }),
    stale: async queryClient => { await queryClient.invalidateQueries({ predicate: q => isConsumableQuery(q.queryKey) }) },
  },
  componentAction: {
    add: (request, item, base) => {
      const d = (base.data ?? {}) as { fit?: unknown; remove?: unknown }
      request.componentActions.push({ clientId: item.clientId, fit: d.fit, remove: d.remove })
    },
    stale: async queryClient => { await queryClient.invalidateQueries({ predicate: q => isConsumableQuery(q.queryKey) }) },
  },
}

export function buildSyncRequest(items: MutationQueueItem[]): SyncRequest {
  const request: SyncRequest = {
    stoppages: [], loadings: [], diggingPatterns: [], completeShifts: [], geoRehandles: [], operationLocations: [], engineHours: [],
    drillingSessions: [], drillingPatterns: [], drillingLines: [], operationMeters: [], gcLines: [], drilledHoles: [], holeDips: [], holeCharges: [], explosiveMovements: [], firings: [],
    misfires: [], consumableMovements: [], componentActions: [],
  }
  for (const item of items) {
    SYNC_ENTITIES[item.entity]?.add(request, item, {
      clientId: item.clientId,
      operation: operationCode(item.operation),
      shiftId: item.shiftId,
      rowVersion: item.rowVersion,
      data: convertDatesToUTC(item.data),
    })
  }
  return request
}

export async function invalidateStale(queryClient: QueryClient, entity: OfflineEntity) {
  const stale = SYNC_ENTITIES[entity]?.stale ?? [['references', 'blastmasterIds'], ['dataTable', 'chatree', 'diggerLocations']]
  if (typeof stale === 'function') {
    await stale(queryClient)
    return
  }
  for (const queryKey of stale)
    await queryClient.invalidateQueries({ queryKey })
}

// Note: the order the server applies a batch in, so a change sent alone after a failed batch lands where the batch would have put it.
const APPLY_ORDER = [
  'drillingSession', 'drillingPattern', 'drillingLine', 'drilledHole', 'gcLine', 'operationMeter', 'holeDip', 'holeCharge', 'firing', 'misfire',
  'drillingPatternRemoval', 'explosiveMovement', 'componentAction', 'consumableMovement',
] as const

const applyRank = (item: MutationQueueItem) =>
  (APPLY_ORDER as readonly string[]).indexOf(item.entity === 'drillingPattern' && item.operation === 'delete' ? 'drillingPatternRemoval' : item.entity) + 1

export const inApplyOrder = (items: MutationQueueItem[]) => [...items].sort((a, b) => applyRank(a) - applyRank(b))

export const childReferences = (): [OfflineEntity, Child[]][] =>
  (Object.entries(SYNC_ENTITIES) as [OfflineEntity, SyncEntity][])
    .filter(([, e]) => e.children)
    .map(([parent, e]) => [parent, e.children!])
